import * as React from 'react';
import {createContext, useState} from 'react';

import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Button from '@mui/material/Button';

import {AllStates, ReactState, StepForm, TranslateFunction} from "./Forms";
import Stack from "@mui/material/Stack";
import {MappingsForm} from "./mappings/MappingsForm";
import MappingsPage from "./mappings/MappingsPage";
import {Alert, Paper, Typography} from "@mui/material";

import FromMarkdown, {SMALL_WINDOW_WIDTH} from "./Representation";

import {useTolgee, useTranslate} from "@tolgee/react";
import FinishPage from "./finish/FinishPage";
import {FormExplanationDialog} from "./FormExplanationDialog";
import {INCOMPLETE_COLOR} from "./Dashboard";

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

interface WizardProps {
    stepForms: StepForm[],
    allStates: AllStates,
    activeStepState: ReactState<number>,
    windowWidth: number
}

export function isPreviousStepsIncomplete(step: number, stepForms: StepForm[], allStates: AllStates, t: TranslateFunction) {
    return stepForms.filter((_stepForm, index) => (index < step))
        .some((stepForm, index) => (!stepForm.isValid(allStates.ordered[index][0], allStates, t)));
}

export default function Wizard(
    {
        stepForms,
        allStates,
        activeStepState,
        windowWidth
    }: WizardProps) {

    const {t} = useTranslate();
    const tolgee = useTolgee();
    const lang = tolgee.getLanguage() ?? "en";

    const [activeStep, setActiveStep] = activeStepState;

    const [formExplanationDialogOpen, setFormExplanationDialogOpen] = useState(false);

    const [pageDialogsOpen, setPageDialogsOpen] = allStates.stepsDialogsOpen;

    const handleShowPage = () => {
        setPageDialogsOpen(allStates.showStepDialogs[0]);
    }
    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        handleShowPage()
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
        handleShowPage()
    };

    const handleStep = (step: number) => () => {
        setActiveStep(step);
        handleShowPage()
    };

    const isCompleted = (step: number) => {
        if (step >= stepForms.length) {
            return false;
        }
        return stepForms[step].isValid(allStates.ordered[step][0], allStates, t);
    };

    const pageFor = (step: number, windowWidth: number) => {
        if (step === stepForms.length) {
            // finish page
            return <FinishPage
                allStates={allStates}
                allStepForms={stepForms}
            />;
        } else if (stepForms[step] instanceof MappingsForm) {
            return <MappingsPage
                mappingsState={allStates.ordered[step]}
                allStates={allStates}
                windowWidth={windowWidth}
            />
        } else {
            return stepForms[step].getPage(allStates.ordered[step], allStates, t, lang);
        }
    };

    const navigationButtons = () => {
        return (<Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
            <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{mr: 1}}
            >
                {t("back")}
            </Button>
            <Box sx={{flex: '1 1 auto'}}/>
            {activeStep === stepForms.length ? (
                <React.Fragment/>
            ) : (
                <Button onClick={handleNext} disabled={!isCompleted(activeStep)}>
                    {activeStep === stepForms.length - 1 ? t("finish") : t("next")}
                </Button>
            )}
        </Box>);
    }

    let page;

    try {
        page = <Box style={{overflow: 'auto'}} padding={1}>{pageFor(activeStep, windowWidth)}</Box>;
    } catch (e) {
        console.error(e);
        page = <React.Fragment>
            <Typography>There is a problem in the current wizard page, which is possibly due to inconsistent data that
                has been input.
                Please press the reset button to clear all form data and start filling in the form from the beginning.
                Pay attention to the documentation of each form field and only try to enter sensible data.
                If the problem persists, you can try one one of the following:
            </Typography>
            <Typography>
                You can try to open this web page with a different browser.
                It is tested with Chrome, Firefox and Safari and you should use a recent version.
                You can also try to clear your browser data and reload this this web page,
                in case it has reached an inconsistent internal state.
            </Typography>
        </React.Fragment>;
    }


    const handlePageDialogClose = () => {
        const newPageDialogsOpen = [...pageDialogsOpen];
        newPageDialogsOpen[activeStep] = false;
        setPageDialogsOpen(newPageDialogsOpen);
    };

    return (
        <React.Fragment>
            <Box sx={{width: '100%'}}>
                <Stack spacing={2}>
                    <Stepper nonLinear activeStep={activeStep} alternativeLabel>
                        {stepForms.map((stepForm, index) => (
                            <Step key={stepForm.getTitleKey()} completed={isCompleted(index)}
                                  disabled={isPreviousStepsIncomplete(index, stepForms, allStates, t)}
                                  sx={{
                                      '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel':
                                          {
                                              color: 'common.black', // Just text label (ACTIVE)
                                          },
                                      '& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel':
                                          {
                                              color: 'common.black', // Just text label (ACTIVE)
                                          },
                                      '& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel':
                                          {
                                              color: 'common.black', // Just text label (COMPLETED)
                                          },
                                      '& .MuiStepLabel-root .Mui-completed': {
                                          color: 'info.main', // circle color (COMPLETED)
                                      },
                                      '& .MuiStepLabel-root .Mui-active': {
                                          color: 'secondary.main', // circle color (ACTIVE)
                                      },
                                      '& .Mui-disabled .MuiStepIcon-root': {
                                          color: INCOMPLETE_COLOR.main, // circle color (ACTIVE)
                                      },
                                      '& .MuiStepLabel-root .Mui-active .MuiStepIcon-text': {
                                          fill: 'secondary.contrastText', // circle's number (ACTIVE)
                                      }
                                  }}
                            >
                                <StepButton color="inherit" onClick={handleStep(index)}>
                                    {windowWidth > SMALL_WINDOW_WIDTH ? t(stepForm.getTitleKey()) : ""}
                                </StepButton>
                            </Step>
                        ))}
                    </Stepper>
                    <React.Fragment>
                        {navigationButtons()}
                        <Stack direction="row" spacing={2}>
                            <Box sx={{flex: '1 1 auto'}}>
                                <Paper sx={{p: 1}} elevation={3}>
                                    <Alert severity="info">
                                        <FromMarkdown>
                                            {(activeStep < stepForms.length) ?
                                                t(stepForms[activeStep].getExplanationMarkdownKey()) :
                                                t("finish.explanation.markdown")
                                            }
                                        </FromMarkdown>
                                    </Alert>
                                </Paper>
                            </Box>
                            <Stack spacing={2}>
                                {
                                    (activeStep < stepForms.length) ?
                                        <React.Fragment>
                                            <Dialog
                                                open={pageDialogsOpen[activeStep]}
                                                onClose={handlePageDialogClose}
                                                aria-labelledby="alert-dialog-title"
                                                aria-describedby="alert-dialog-description"
                                            >
                                                <DialogTitle id="alert-dialog-title">
                                                    { t("page_dialog_title_" + stepForms[activeStep].getTitleKey()) }
                                                </DialogTitle>
                                                <DialogContent>
                                                    <Alert severity="warning">
                                                        <FromMarkdown>
                                                            { t("page_dialog_body_" + stepForms[activeStep].getTitleKey() + ".markdown") }
                                                        </FromMarkdown>
                                                    </Alert>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={!allStates.showStepDialogs[0][activeStep]}
                                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                    const newShowStepDialogs = [...allStates.showStepDialogs[0]];
                                                                    newShowStepDialogs[activeStep] = !event.target.checked;
                                                                    allStates.showStepDialogs[1](newShowStepDialogs);
                                                                }}
                                                            />} label={t("dont_show_again")} />
                                                </DialogContent>
                                                <DialogActions>
                                                    <Button onClick={handlePageDialogClose} autoFocus>
                                                        { t("understood") }
                                                    </Button>
                                                </DialogActions>
                                            </Dialog>
                                            <FormExplanationDialog
                                                openState={[formExplanationDialogOpen, setFormExplanationDialogOpen]}
                                                errors={stepForms[activeStep].getFilteredValidationErrors(allStates.ordered[activeStep][0], allStates, t) ?? []}
                                                schema={stepForms[activeStep].getSchema()}
                                                formNs={stepForms[activeStep].getFormNs() ?? ""}
                                            />
                                        </React.Fragment> :
                                        <React.Fragment/>
                                }
                            </Stack>
                        </Stack>
                        {page}
                        {navigationButtons()}
                    </React.Fragment>
                </Stack>
            </Box>
        </React.Fragment>
    );
}

export const ArrayErrorMessagesContext = createContext<string[]>([]);